import { PlayCircleOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import styled from '@emotion/styled';
import { Button, Modal, Typography } from 'antd';
import { useCurrentChatBuyerLang } from 'hooks/business/im/chatPreference';
import useIM from 'hooks/business/im/useIM';
import { getTextVideoTextContent, UploadStateChangeEvent } from 'hooks/business/sendMessage';
import { useEffect, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { checkResourceIsExpired, HistoryResource } from 'services/api/resource';
import nimInst from 'services/nim';
import { useChatConversationContext } from 'views/Conversation/Chat/contexts/ChatConversationContext';
import ProxyVideo from 'components/ProxyVideo';

const { Text } = Typography;

const Container = styled.div`
  width: 128px;
  border: 1px solid rgba(240, 242, 245, 1);
  border-radius: 2px;
  padding: 5px;
  height: fit-content;
  .video-box {
    display: flex;
    background: rgba(237, 245, 255, 1);
    border-radius: 2px;
    position: relative;
    cursor: pointer;
    .thumb {
      width: 118px;
      height: 88px;
      object-fit: cover;
    }
    .duration {
      position: absolute;
      bottom: 6px;
      right: 12px;
      color: #ffffff;
      font-size: 12px;
      background: rgba(35, 36, 38, 1);
      border-radius: 12px;
      opacity: 0.47;
      padding: 3px 6px;
    }
    .play {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      color: #ffffff;
      z-index: 10;
      svg {
        width: 16px;
        height: 16px;
      }
    }
  }
  .video-name {
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 12px;
    color: #515359;
    height: 18px;
    line-height: 18px;
    margin: 6px 0;
  }
  .video-action {
    button {
      background: rgba(215, 225, 250, 1);
      border-radius: 2px;
      color: #336fff;
      width: 100%;
    }
  }
`;
const modalCss = css`
  top: 0;
  width: 100vw !important;
  max-width: 100vw;
  position: fixed;
  bottom: 0;
  right: 0;
  left: 0;
  padding: 0;
  margin: 0;
  .ant-modal-content {
    width: 100%;
    overflow: hidden;
    height: 100%;
    .ant-modal-body {
      width: 100%;
      height: 100%;
      height: calc(100vh - 55px);
      padding: 0;
      overflow: auto;
      background-color: #eef0f4;
    }
  }
`;
const ModalContent = styled.div`
  width: 100%;
  margin: 0;
  height: 100%;
  padding: 20px;
  video {
    margin: 0;
    width: 100%;
    height: 100%;
  }

  .right {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding-left: 7px;
    color: #84868c;
    a {
      padding: 0;
      text-align: left;
    }
  }
`;
interface Props {
  item: HistoryResource['textVideoContent']['videoContent'] | HistoryResource['videoContent'];
  preview?: boolean;
}

const VideoCard = (props: Props) => {
  const { item, preview } = props;
  const { videoName, localVideoUrl, thumbUrl, durationSeconds, resourceId } = item;
  const [play, setPlay] = useState(false);
  const { sendTextVideo, sendVideo } = useIM();
  const { currentConversation } = useChatConversationContext();
  const lang = useCurrentChatBuyerLang(currentConversation?.region || 'ID');
  const [videoStatus, setVideoStatus] = useState<'success' | 'uploading' | 'error'>('success');
  const [sending, setSending] = useState(false);
  const [updateInfo, setUpdateInfo] = useState(item);

  const { t } = useTranslate();

  useEffect(() => {
    return nimInst.on('message', (message: any) => {
      if (message.resourceId) {
        const msg = message as UploadStateChangeEvent;
        if (`${msg.resourceId}` === `${item.resourceId}`) {
          setUpdateInfo((pre: any) => {
            return {
              ...pre,
              videoUrl: msg.url,
            };
          });
          setVideoStatus(msg.resourceState === 4 ? 'success' : 'error');
        }
      }
    });
  }, [item.resourceId]);
  const duration = () => {
    const minute = Math.floor(durationSeconds / 60);
    const second = durationSeconds % 60;
    return `${minute.toString().padStart(2, '0')}:${second.toString().padStart(2, '0')}`;
  };
  const onSend = async () => {
    setSending(true);
    try {
      const ret = await checkResourceIsExpired(resourceId);
      if (ret.state === 4) {
        const vid = item.vid;
        if (vid) {
          // shopee 发送视频
          await sendVideo(currentConversation?.conversationId!, {
            vid,
            videoUrl: updateInfo.videoUrl,
            localVideoUrl: updateInfo.localVideoUrl,
            videoName: updateInfo.videoName,
            thumbUrl: updateInfo.thumbUrl,
            durationSeconds: updateInfo.durationSeconds,
            videoId: '',
          });
        } else {
          await sendTextVideo(currentConversation?.conversationId!, {
            textContent: getTextVideoTextContent(updateInfo.videoUrl, lang),
            videoContent: updateInfo,
          });
        }
      } else {
        setVideoStatus('uploading');
      }
    } catch (error) {
      //
    }
    setSending(false);
  };
  const btnText = () => {
    if (videoStatus === 'error') {
      return t('ViewsChat.点击重试');
    }
    if (videoStatus === 'uploading') {
      return t('ViewsChat.传输中');
    }
    return t('ViewsChat.发送');
  };
  return (
    <Container>
      <div className="video-box" onClick={() => setPlay(true)}>
        <img src={thumbUrl} alt="" className="thumb" />
        <PlayCircleOutlined className="play" />
        <div className="duration">{duration()}</div>
      </div>
      <Text ellipsis={{ tooltip: videoName }}>{videoName}</Text>
      {preview ? null : (
        <div className="video-action">
          <Button onClick={onSend} disabled={videoStatus === 'uploading' || sending}>
            {btnText()}
          </Button>
        </div>
      )}
      <Modal
        width={700}
        visible={play}
        title={videoName}
        destroyOnClose
        footer={null}
        zIndex={99999}
        className={modalCss}
        onCancel={() => setPlay(false)}
      >
        <ModalContent>
          <ProxyVideo controls autoPlay src={localVideoUrl} controlsList="nodownload">
            <track kind="captions" />
          </ProxyVideo>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default VideoCard;
